import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelOrder, cancelOrderItem } from "../endpoints/order";
import type { CancelOrderItemParam, CancelOrderParam } from "../types/order";

/**
 * `POST /order/cancel` — self-service cancel of a whole order (#264).
 * On success invalidates the order list + the specific order detail so the
 * status pill flips to CANCELLED and the cancel button disappears.
 */
export function useCancelOrder(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CancelOrderParam) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return cancelOrder({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["orders", "list"] });
      qc.invalidateQueries({ queryKey: ["orders", "detail", input.orderNo] });
    },
  });
}

/**
 * `POST /order/orderItemCancel` — cancel a single order item / sub-order (#264).
 * Invalidates both the list and every order detail (the item may belong to any
 * cached order) so the affected order/item refreshes.
 */
export function useCancelOrderItem(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CancelOrderItemParam) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return cancelOrderItem({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "list"] });
      qc.invalidateQueries({ queryKey: ["orders", "detail"] });
    },
  });
}
